/**
 * The connectivity panel's view model: the state (`state.ts`) and the
 * operator message (`describe.ts`) folded into plain rows the presentational
 * components render (`components/ConnectivityStatus.tsx`). Pure — a function
 * of the state and the clock — so what the panel shows is unit-tested here,
 * and the stories build their states from fixtures of this shape.
 */
import { describeWitness } from '@acp/api-contract/witness.js';
import { describeConnectivity, type ConnectivitySummary } from './describe.js';
import { holdsLocalProbes, type LocalNetworkAccess } from './localNetwork.js';
import { hintFor, type ReachHint } from './reachHint.js';
import { type ConnectivityState, type EndpointStatus, localOffer, type PairSummary, type Reach, RETRY_MAX_MS, type SocketState } from './state.js';
import type { CloudLinkStateWire } from '@acp/api-contract/primitives.js';

export type EndpointRow = {
  readonly id: string;
  readonly kind: 'cloud' | 'node';
  readonly label: string;
  readonly url: string;
  readonly primary: boolean;
  readonly reach: Reach;
  readonly latencyMs: number | null;
  readonly version: string | null;
  readonly stage: string | null;
  /** A node's own site link; null for the cloud or before it answered. */
  readonly cloudLink: CloudLinkStateWire | null;
  readonly outboxDepth: number | null;
  /** A node of a pair: the witness in force and the expected failover (`lan (~1.0 s)`, wave 4c); null otherwise. */
  readonly witness: string | null;
  /** A node of a pair: its place, what it runs active and standby, the peer link (`main · active 2 · standby 1 · peer connected`); null otherwise. */
  readonly pair: string | null;
  /** "12 s ago" since the last answer, when it is not answering now; null otherwise. */
  readonly lastSeen: string | null;
  /** "in 4 min": when a dormant endpoint (`probeDelayMs`, backed off past the usual retry) is tried again; null otherwise. */
  readonly nextCheck: string | null;
  readonly error: string | null;
  /** A node the cloud says is connected but this browser cannot reach: what is probably wrong (`reachHint.ts`); null otherwise. */
  readonly hint: ReachHint | null;
  /** The productions a node runs that the cloud does not know as its site's, one line each with the way out; empty for none. */
  readonly cloudRefusals: readonly string[];
};

export type CloudViewRow = { readonly siteId: string; readonly nodeId: string; readonly role: string; readonly linkState: string; readonly lastSeen: string };

export type ConnectivityView = {
  readonly summary: ConnectivitySummary;
  readonly primary: { readonly id: string; readonly kind: 'cloud' | 'node'; readonly label: string; readonly latencyMs: number | null };
  readonly socket: SocketState;
  readonly endpoints: readonly EndpointRow[];
  /** "Switch to local" (PLAN §10.2): the node to offer, or null. */
  readonly offerLocal: { readonly id: string; readonly label: string } | null;
  /** The operator chose a primary; "use the default" undoes it. */
  readonly chosen: boolean;
  /** The cloud's view of the tenant's sites and nodes; null when it could not be read. */
  readonly cloudView: readonly CloudViewRow[] | null;
  /** Chrome's Local Network Access for this page (`localNetwork.ts`), and whether to offer "Connect to local servers". */
  readonly localNetwork: LocalNetworkView;
};

export type LocalNetworkView = {
  readonly access: LocalNetworkAccess;
  /** The permission holds a studio server back that has not answered: offer the button (a click lets Chrome ask). */
  readonly offerConnect: boolean;
};

/** "Connect to local servers" is offered while the permission holds back a server that has not answered. */
export function localNetworkView(state: Pick<ConnectivityState, 'endpoints' | 'localNetwork'>): LocalNetworkView {
  const waiting = state.endpoints.some((s) => s.endpoint.kind === 'node' && s.reach !== 'up');
  return { access: state.localNetwork, offerConnect: holdsLocalProbes(state.localNetwork) && waiting };
}

/** A duration for people: `4 s`, `3 min`, `2 h`. */
export function ago(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${String(s)} s ago`;
  const min = Math.round(s / 60);
  if (min < 60) return `${String(min)} min ago`;
  return `${String(Math.round(min / 60))} h ago`;
}

/** A time ahead for people: `in 20 s`, `in 4 min`. */
export function inTime(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return s < 60 ? `in ${String(s)} s` : `in ${String(Math.round(s / 60))} min`;
}

/** When a down endpoint whose probes have backed off past the usual retry is tried next; null while it is retried as usual. */
export function nextCheckOf(s: EndpointStatus, nowMs: number): string | null {
  if (s.reach === 'up' || s.nextProbeAtMs === null || s.nextProbeAtMs - nowMs <= RETRY_MAX_MS) return null;
  return inTime(s.nextProbeAtMs - nowMs);
}

/** A node's pair in a line: its place, how many productions it runs active and standby, the peer link, any alarms. */
export function describePair(pair: PairSummary): string {
  const alarms = pair.alarms === 0 ? '' : ` · ${String(pair.alarms)} alarm${pair.alarms === 1 ? '' : 's'}`;
  return `${pair.nodeRole} · active ${String(pair.active)} · standby ${String(pair.standby)} · peer ${pair.peerLink}${alarms}`;
}

export function connectivityView(state: ConnectivityState, nowMs: number): ConnectivityView {
  const endpoints: EndpointRow[] = state.endpoints.map((s) => ({
    id: s.endpoint.id,
    kind: s.endpoint.kind,
    label: s.endpoint.label,
    url: s.endpoint.apiUrl,
    primary: s.endpoint.id === state.primaryId,
    reach: s.reach,
    latencyMs: s.latencyMs,
    version: s.version,
    stage: s.stage,
    cloudLink: s.node?.cloudLink ?? null,
    outboxDepth: s.node?.outboxDepth ?? null,
    witness: s.node?.witness === null || s.node?.witness === undefined ? null : describeWitness(s.node.witness),
    pair: s.node?.pair === null || s.node?.pair === undefined ? null : describePair(s.node.pair),
    lastSeen: s.reach !== 'up' && s.lastOkAtMs !== null ? ago(nowMs - s.lastOkAtMs) : null,
    nextCheck: nextCheckOf(s, nowMs),
    error: s.error,
    hint: hintFor(state, s),
    cloudRefusals: (s.node?.cloudRefusals ?? []).map((r) => `Production ${r.productionId} is not known to the cloud: ${r.reason}. The way out: ${r.fix}`),
  }));
  const primaryRow = endpoints.find((e) => e.primary) ?? endpoints[0];
  const offerId = localOffer(state);
  const offer = offerId === null ? null : (endpoints.find((e) => e.id === offerId) ?? null);
  return {
    summary: describeConnectivity(state),
    primary: primaryRow === undefined ? { id: state.primaryId, kind: 'cloud', label: '?', latencyMs: null } : { id: primaryRow.id, kind: primaryRow.kind, label: primaryRow.label, latencyMs: primaryRow.latencyMs },
    socket: state.socket,
    endpoints,
    offerLocal: offer === null ? null : { id: offer.id, label: offer.label },
    chosen: state.chosenId !== null,
    cloudView:
      state.cloudView === null
        ? null
        : state.cloudView.sites.flatMap((site) =>
            site.nodes.map((n) => ({ siteId: site.siteId, nodeId: n.nodeId, role: n.role, linkState: n.linkState, lastSeen: n.lastSeen === null ? 'never' : ago(nowMs - Date.parse(n.lastSeen)) })),
          ),
    localNetwork: localNetworkView(state),
  };
}
